import { api } from './api';
import { unwrapList } from './guest';

export interface GuestCharge {
  id: string;
  description: string;
  quantity: string;
  unitPrice: string;
  amount: string;
  taxAmount: string;
  voidedAt: string | null;
}

export interface GuestCheck {
  id: string;
  number: string;
  /** The visit this bill belongs to. Null only for a walk-up or takeaway check. */
  sessionId: string | null;
  isMaster: boolean;
  subtotal: string;
  discountTotal: string;
  serviceTotal: string;
  taxTotal: string;
  tipTotal: string;
  grandTotal: string;
  paidTotal: string;
  currency: string;
  charges?: GuestCharge[];
}

/**
 * Every bill this visit can reach, in one question.
 *
 * `GET /checks?sessionId=` is the guest bill endpoint, and the server answers it
 * as "the checks this session's own orders were routed to, plus the checks
 * opened for the visit itself". That is the whole of a guest's reach: the group
 * seated at this table before us has its own session and its own bills, and
 * neither is visible from here.
 *
 * The id is sent for readability only — `GuestScopeGuard` overwrites it from the
 * token before the handler sees it, so a guest cannot ask for another visit's
 * checks by editing the request. This used to walk the guest's PARTY when it had
 * one and fall back to reading the orders for their check ids when it did not;
 * neither hop exists any more, because the check now names the session outright.
 *
 * The list endpoint does not expand charges, so each check is then fetched whole.
 */
export async function loadGuestChecks(sessionId: string): Promise<GuestCheck[]> {
  const expand = async (id: string): Promise<GuestCheck | null> => {
    try {
      const { data } = await api.get<GuestCheck>(`/checks/${id}`);
      return data;
    } catch {
      return null;
    }
  };

  const { data } = await api.get('/checks', { params: { sessionId } });
  const ids = unwrapList<GuestCheck>(data).map((check) => check.id);

  const checks = await Promise.all(ids.map(expand));
  return checks.filter((check): check is GuestCheck => check !== null);
}

/** What the whole visit still owes, across every bill opened for it. */
export function visitDue(checks: GuestCheck[]): number {
  return checks.reduce(
    (sum, check) => sum + (Number(check.grandTotal) - Number(check.paidTotal)),
    0,
  );
}

/** What the whole visit has been charged, across every bill opened for it. */
export function visitTotal(checks: GuestCheck[]): number {
  return checks.reduce((sum, check) => sum + Number(check.grandTotal), 0);
}
